import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import StatCard from '../../components/cards/StatCard';
import './Dashboard.css';
import { getApi } from '../../services/api';

const Dashboard = () => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  const getProfile = async () => {
    try {
      const response = await getApi('profile');
      if (response.data?.status === true || response.status === true) {
        setUserData(response.data?.USER || response.USER);
      } else {
        navigate('/login');
      }
    } catch (error) {
      console.error(error);
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { getProfile(); }, []);

  const stats = [
    { icon: 'bi-wallet2', title: 'Wallet Balance', value: userData?.available_amount || 0, color: 'primary', prefix: '$' },
    { icon: 'bi-layers', title: 'Staked Amount', value: userData?.today_income || 0, color: 'success', prefix: '$' },
    { icon: 'bi-gift', title: "Today's Reward", value: userData?.today_income || 0, color: 'warning', prefix: '$' },
    { icon: 'bi-cash-stack', title: 'Total Income', value: userData?.total_income || 0, color: 'primary', prefix: '$' },
    { icon: 'bi-trophy', title: 'Current Rank', value: userData?.rank || '—', color: 'success' },
    { icon: 'bi-people', title: 'Direct Referral', value: userData?.my_referral || 0, color: 'warning' },
  ];

  const totalUser = (userData?.left_users || 0) + (userData?.right_users || 0);
  const treeBusiness = (userData?.left_business || 0) + (userData?.right_business || 0);

  const leftPct = totalUser > 0 ? ((userData?.left_users || 0) / totalUser) * 100 : 0;
  const rightPct = totalUser > 0 ? ((userData?.right_users || 0) / totalUser) * 100 : 0;
  const leftBiz = treeBusiness > 0 ? ((userData?.left_business || 0) / treeBusiness) * 100 : 0;
  const rightBiz = treeBusiness > 0 ? ((userData?.right_business || 0) / treeBusiness) * 100 : 0;

  const codeDisplay = userData?.my_code
    ? `${userData.my_code.slice(0, 6)}••••${userData.my_code.slice(-4)}`
    : '—';

  if (loading) {
    return (
      <div className="dashboard">
        {/* Skeleton Loading */}
        <div className="p-card mb-4" style={{ padding: '2rem' }}>
          <div className="skeleton mb-3" style={{ width: 200, height: 28 }}></div>
          <div className="skeleton" style={{ width: 140, height: 18 }}></div>
        </div>
        <div className="row g-3 mb-4">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} className="col-lg-4 col-md-6">
              <div className="skeleton" style={{ height: 100, borderRadius: 18 }}></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      {/* Welcome Banner */}
      <div className="p-card mb-4" style={{
        background: 'linear-gradient(135deg, rgba(0,255,149,0.06) 0%, rgba(0,212,255,0.04) 50%, rgba(168,85,247,0.03) 100%)',
        borderColor: 'rgba(0,255,149,0.2)',
        padding: '1.75rem 2rem',
      }}>
        <div className="row align-items-center g-3">
          <div className="col-lg-7">
            <div className="d-flex align-items-center gap-3 mb-2">
              <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'linear-gradient(135deg, rgba(0,255,149,0.3), rgba(0,212,255,0.2))', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i className="bi bi-person" style={{ color: 'var(--page-accent)' }}></i>
              </div>
              <div>
                <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Welcome back</p>
                <h4 style={{ margin: 0, fontWeight: 800, fontFamily: 'JetBrains Mono, monospace', color: 'white' }}>{codeDisplay}</h4>
              </div>
            </div>
            <p style={{ color: 'var(--text-secondary)', margin: 0, fontSize: '0.9rem' }}>
              Your portfolio is growing daily. Current rank:{' '}
              <strong style={{ color: 'var(--page-accent)' }}>{userData?.rank || 'Member'}</strong>
            </p>
          </div>
          <div className="col-lg-5">
            <div className="d-flex gap-2 justify-content-lg-end flex-wrap">
              <Link to="/dashboard/deposit" className="btn btn-gradient" style={{ borderRadius: 12, fontWeight: 700, padding: '0.65rem 1.4rem' }}>
                <i className="bi bi-arrow-down-circle me-2"></i>Deposit
              </Link>
              <Link to="/dashboard/withdraw" className="btn" style={{ background: 'rgba(255,255,255,0.06)', color: 'white', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, fontWeight: 700, padding: '0.65rem 1.4rem', textDecoration: 'none' }}>
                <i className="bi bi-arrow-up-circle me-2"></i>Withdraw
              </Link>
              <Link to="/dashboard/stake" className="btn" style={{ background: 'rgba(0,212,255,0.1)', color: 'var(--page-blue)', border: '1px solid rgba(0,212,255,0.25)', borderRadius: 12, fontWeight: 700, padding: '0.65rem 1.4rem', textDecoration: 'none' }}>
                <i className="bi bi-layers me-2"></i>Stake
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="row g-3 mb-4">
        {stats.map((stat, index) => (
          <div key={index} className="col-lg-4 col-md-6">
            <StatCard {...stat} />
          </div>
        ))}
      </div>

      {/* Network Overview */}
      <div className="row g-4 mb-4">
        {/* Left Branch */}
        <div className="col-lg-6">
          <div className="p-card">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <p className="section-label mb-0">Binary Tree</p>
                <h5 style={{ margin: 0, fontWeight: 700 }}>Left Branch</h5>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '1.4rem', fontWeight: 800, fontFamily: 'JetBrains Mono, monospace', color: 'var(--page-accent)' }}>{userData?.left_users || 0}</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>total members</div>
              </div>
            </div>

            <div className="p-progress-wrap">
              <div className="p-progress-header">
                <span className="p-progress-label">Left Users</span>
                <span className="p-progress-value">{userData?.left_users || 0} ({leftPct.toFixed(1)}%)</span>
              </div>
              <div className="p-progress">
                <div className="p-progress-bar" style={{ width: `${leftPct}%` }}></div>
              </div>
            </div>

            <div className="p-progress-wrap mt-3">
              <div className="p-progress-header">
                <span className="p-progress-label">Left Business</span>
                <span className="p-progress-value">${userData?.left_business || 0} ({leftBiz.toFixed(1)}%)</span>
              </div>
              <div className="p-progress">
                <div className="p-progress-bar" style={{ width: `${leftBiz}%`, background: 'linear-gradient(90deg, var(--page-warning), #ff9800)' }}></div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Branch */}
        <div className="col-lg-6">
          <div className="p-card">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <p className="section-label mb-0">Binary Tree</p>
                <h5 style={{ margin: 0, fontWeight: 700 }}>Right Branch</h5>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '1.4rem', fontWeight: 800, fontFamily: 'JetBrains Mono, monospace', color: 'var(--page-blue)' }}>{userData?.right_users || 0}</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>total members</div>
              </div>
            </div>

            <div className="p-progress-wrap">
              <div className="p-progress-header">
                <span className="p-progress-label">Right Users</span>
                <span className="p-progress-value">{userData?.right_users || 0} ({rightPct.toFixed(1)}%)</span>
              </div>
              <div className="p-progress">
                <div className="p-progress-bar" style={{ width: `${rightPct}%`, background: 'linear-gradient(90deg, var(--page-blue), var(--page-purple))' }}></div>
              </div>
            </div>

            <div className="p-progress-wrap mt-3">
              <div className="p-progress-header">
                <span className="p-progress-label">Right Business</span>
                <span className="p-progress-value">${userData?.right_business || 0} ({rightBiz.toFixed(1)}%)</span>
              </div>
              <div className="p-progress">
                <div className="p-progress-bar" style={{ width: `${rightBiz}%`, background: 'linear-gradient(90deg, var(--page-purple), #6366f1)' }}></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Links */}
      <div className="p-card">
        <p className="section-label">Quick Actions</p>
        <div className="row g-3">
          {[
            { label: 'Wallet', icon: 'bi-wallet2', href: '/dashboard/wallet', color: 'var(--page-accent)', desc: 'View balances' },
            { label: 'Stake', icon: 'bi-layers', href: '/dashboard/stake', color: 'var(--page-blue)', desc: 'Earn rewards' },
            { label: 'Team', icon: 'bi-people', href: '/dashboard/team', color: 'var(--page-warning)', desc: 'My referrals' },
            { label: 'Rank', icon: 'bi-trophy', href: '/dashboard/rank', color: 'var(--page-purple)', desc: 'Achievements' },
            { label: 'Deposit', icon: 'bi-arrow-down-circle', href: '/dashboard/deposit', color: 'var(--page-accent)', desc: 'Add funds' },
            { label: 'Withdraw', icon: 'bi-arrow-up-circle', href: '/dashboard/withdraw', color: 'var(--page-danger)', desc: 'Cash out' },
          ].map((link, i) => (
            <div key={i} className="col-md-2 col-4">
              <Link to={link.href} style={{ textDecoration: 'none', display: 'block' }}>
                <div style={{
                  background: 'rgba(255,255,255,0.02)',
                  border: '1px solid rgba(255,255,255,0.07)',
                  borderRadius: 16, padding: '1.1rem 0.75rem',
                  textAlign: 'center', transition: 'all 0.25s', cursor: 'pointer',
                }} onMouseEnter={e => { e.currentTarget.style.borderColor = `${link.color}40`; e.currentTarget.style.background = `${link.color}08`; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)'; e.currentTarget.style.background = 'rgba(255,255,255,0.02)'; }}>
                  <i className={`bi ${link.icon}`} style={{ fontSize: '1.4rem', color: link.color, display: 'block', marginBottom: 6 }}></i>
                  <div style={{ fontSize: '0.82rem', fontWeight: 700, color: 'white', marginBottom: 2 }}>{link.label}</div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{link.desc}</div>
                </div>
              </Link>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;